import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Drawer as PennyDrawer,
  Group,
  NavigationItem,
  NavigationList,
  Pill as PennyPill,
  Table as Table_,
  TableCell,
  Text,
  Tooltip,
  useTable,
} from '@melio/penny';

import { useApp, Banner, Icon, IconButton, Illustration, Card, Button, type IconKey } from '@/components/ds';
import { asset } from '@/lib/assets';
import { DASHBOARD_ENV_IDS } from '@/config/environments';
import { SCHEDULE_URL } from '@/config/links';
import { EnvSwitcher, OrgName } from '@/components/live';
import { BusinessesView } from '@/containers/dashboard-businesses';
import { KeysView, WebhooksView } from '@/containers/dashboard-developers';
import { PayoutsSimulator, type SimConnection } from '@/containers/simulator';
import type { Go } from '@/lib/types';

// dashboard.tsx — authenticated portal shell + shared widgets (Drawer, Table, StatCard).

// ---- Drawer (right slide-over) ------------------------------------------
// penny-backed: <Drawer> from @melio/penny. Penny owns the overlay, the slide-in animation, the
// close button and — the part the hand-rolled version was missing — focus trapping, Esc-to-close,
// focus restore and the dialog role/aria wiring. Props are kept as they were so call sites are
// unchanged; `title` maps to penny's `header` and `children` to its `body`.
interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  width?: number;
  style?: CSSProperties;
}
function Drawer({ open, onClose, title, children, footer, width = 540 }: DrawerProps) {
  return (
    <PennyDrawer
      isOpen={open}
      onClose={onClose}
      size={width}
      closeButtonAriaLabel="Close"
      header={
        title ? (
          <Text as="h2" textStyle="heading3Semi">
            {title}
          </Text>
        ) : undefined
      }
      body={children}
      // Penny's DrawerFooter is a flex row with justify-end but no gap of its own, so footer
      // actions would sit flush against each other; the Group supplies the spacing.
      footer={footer ? <Group spacing="s">{footer}</Group> : undefined}
    />
  );
}

// ---- Table --------------------------------------------------------------
// penny-backed: <Table> + useTable from @melio/penny (a @tanstack/react-table integration).
// The call-site API is unchanged — columns/rows/onRow — but penny now owns the grid semantics,
// the sticky header, the mobile breakpoint and the row-click affordance. The previous version
// put an onClick on a bare <tr>, which no keyboard or screen-reader user could reach.
interface TableColumn<T> {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  render?: (row: T) => ReactNode;
}

interface TableProps<T> {
  columns: TableColumn<T>[];
  rows: T[];
  onRow?: (row: T) => void;
  style?: CSSProperties;
}

const ALIGN_MAP = { left: 'start', center: 'center', right: 'end' } as const;

function Table<T extends Record<string, any>>({ columns, rows, onRow }: TableProps<T>) {
  const tableProps = useTable<T>({
    data: rows,
    getRowId: (_row, index) => String(index),
    onRowClick: onRow ? ({ rowData }) => onRow(rowData) : undefined,
    columns: columns.map((c) => ({
      id: c.key,
      header: c.label,
      textAlign: ALIGN_MAP[c.align ?? 'left'],
      cell: ({ row }) => <TableCell textAlign={ALIGN_MAP[c.align ?? 'left']}>{c.render ? c.render(row) : row[c.key]}</TableCell>,
    })),
  });
  return <Table_ {...tableProps} />;
}

interface StatCardProps {
  label: string;
  value: ReactNode;
  sub?: string;
  icon?: IconKey;
  accent?: boolean;
  style?: CSSProperties;
}
function StatCard({ label, value, sub, icon, accent }: StatCardProps) {
  return (
    <Card padding={20}>
      <Group variant="vertical" spacing="xxs">
        <Group variant="horizontal" justifyContent="space-between" alignItems="center">
          <Text textStyle="body4Semi" color="semantic.text.secondary">
            {label}
          </Text>
          {icon && <Icon name={icon} size={18} color={accent ? 'var(--accent)' : 'var(--neutral-600)'} />}
        </Group>
        <Text textStyle="heading2Semi">{value}</Text>
        {sub && (
          <Text textStyle="caption1" color="semantic.text.secondary">
            {sub}
          </Text>
        )}
      </Group>
    </Card>
  );
}

interface PageTitleProps {
  title: ReactNode;
  sub?: string;
  action?: ReactNode;
  style?: CSSProperties;
}
function PageTitle({ title, sub, action }: PageTitleProps) {
  return (
    <Container paddingBottom="l">
      <Group variant="horizontal" alignItems="flex-start" justifyContent="space-between" spacing="m">
        <Group variant="vertical" spacing="xxs">
          <Text as="h1" textStyle="heading1Semi">
            {title}
          </Text>
          {sub && (
            <Text textStyle="body3" color="semantic.text.secondary">
              {sub}
            </Text>
          )}
        </Group>
        {action}
      </Group>
    </Container>
  );
}

// ---- Shell --------------------------------------------------------------
interface RailItemProps {
  label: string;
  icon: IconKey;
  selected?: boolean;
  disabled?: boolean;
  title?: string;
  onClick: () => void;
}
// One row of the dashboard's left rail, on penny's <NavigationItem>. Penny owns the selected,
// hover, focus-visible and disabled treatments — what used to be the same twelve-line inline
// style object repeated for every rail entry.
function RailItem({ label, icon, selected = false, disabled = false, title, onClick }: RailItemProps) {
  const item = (
    <NavigationItem isFullWidth isSelected={selected} onClick={() => !disabled && onClick()} aria-disabled={disabled || undefined}>
      <Group spacing="s" alignItems="center" variant="horizontal">
        <Icon name={icon} size={18} color={selected ? 'var(--accent)' : 'var(--icon-primary)'} />
        <Text textStyle={selected ? 'body3Semi' : 'body3'} color="inherit">
          {label}
        </Text>
      </Group>
    </NavigationItem>
  );
  // A disabled rail entry needs to say *why* it's disabled. `shouldAddTriggerFocus` is penny's
  // answer for exactly this: a disabled control fires no hover events, so the tooltip needs a
  // focusable wrapper to hang off. Beats the old `title` attribute, which screen readers treat
  // inconsistently.
  // NavigationItem exposes no disabled styling, so the dimming lives on a wrapper.
  const wrapped = disabled ? <span style={{ display: 'block', opacity: 0.45, cursor: 'not-allowed' }}>{item}</span> : item;
  return title ? (
    <Tooltip content={title} shouldAddTriggerFocus>
      {wrapped}
    </Tooltip>
  ) : (
    wrapped
  );
}

const DASH_NAV: Array<{
  group: string | null;
  items: Array<[string, string, IconKey]>;
}> = [
  {
    group: null,
    items: [['entities', 'Entities', 'business']],
  },
  {
    group: 'Developers',
    items: [
      ['keys', 'API keys', 'lock'],
      ['webhooks', 'Webhooks', 'fast'],
    ],
  },
];

// Shown in the content area (in place of any dashboard section) when the partner has selected the production
// environment but isn't operational yet — production entities, webhooks and keys are all off-limits until
// they're approved. Offers the beta-program scheduling link and points them back to Test (staging).
function ProductionAccessPending() {
  return (
    <Card padding={40} style={{ textAlign: 'center', maxWidth: 560, margin: '24px auto' }}>
      <Group variant="vertical" spacing="s" alignItems="center">
        <Illustration name="blocked" size={96} />
        <Text as="h2" textStyle="heading2Semi">
          Production access pending
        </Text>
        <Text textStyle="body2" color="semantic.text.secondary">
          Your partner isn’t approved for production yet, so entities, webhooks and API keys can’t be managed in live mode. You can keep
          building in <strong>Test (staging)</strong> — switch the environment above.
        </Text>
        <Text textStyle="body2" color="semantic.text.secondary">
          Ready to go live? Book a call and we’ll walk through getting you approved for production.
        </Text>
        <Container paddingTop="s">
          <Button size="large" rightIcon="arrow-right" onClick={() => window.open(SCHEDULE_URL, '_blank', 'noopener,noreferrer')}>
            Schedule a call
          </Button>
        </Container>
      </Group>
    </Card>
  );
}

interface DashboardProps {
  go: Go;
  style?: CSSProperties;
}
function Dashboard({ go }: DashboardProps) {
  const app = useApp();
  const { view: viewParam, id } = useParams();
  const view = viewParam || 'entities';
  // Production is locked until the partner is operational — every dashboard section (entities, webhooks,
  // keys) hits a production resource, so we gate the whole shell here rather than per-view. `operational` is
  // undefined until `/me` resolves; treat that as locked so we never briefly expose live controls.
  const prodLocked = app.isLive && !app.operational;
  // View switches are real navigations so each dashboard section has its own URL.
  const setView = (id: string) => go(`/dashboard/${id}`);

  // Live connection reported by the embedded Simulator (its own partner key drives its env). The
  // Simulator shows the env and partner in its own console header, so the shell doesn't repeat it —
  // this is only tracked so the rail can lock while a live session is held (see simLock).
  const [simConn, setSimConn] = useState<SimConnection | null>(null);
  const inSimulator = !prodLocked && view === 'simulator';
  // While the Simulator holds a live partner-key connection, lock the surrounding dashboard so the
  // user can't navigate away (and silently drop that session) without first disconnecting from the
  // Simulator's own rail. Clears the moment they disconnect (onConnectionChange → null).
  const simLock = inSimulator && !!simConn;

  // The dashboard operates only against the envs it can manage (staging01, production); if the app
  // landed on another env (e.g. from the host default), snap to staging01 so the switcher is valid.
  // The Simulator is exempt: it connects with its own partner key and may target other envs
  // (eilat / local), which it drives itself — snapping would fight it. Leaving the section
  // re-runs this and self-corrects the env for the token-authenticated tabs.
  useEffect(() => {
    if (view !== 'simulator' && !DASHBOARD_ENV_IDS.includes(app.env)) app.setEnv('staging01');
  }, [app.env, app.setEnv, view]);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--neutral-200)' }}>
      {/* left rail */}
      <aside
        style={{
          width: 244,
          flexShrink: 0,
          background: 'var(--surface-primary)',
          borderRight: '1px solid var(--border-static)',
          display: 'flex',
          flexDirection: 'column',
          padding: '16px 12px',
        }}
      >
        <button
          onClick={() => !simLock && go('/')}
          disabled={simLock}
          aria-disabled={simLock}
          title={simLock ? 'Disconnect the simulator to leave the dashboard' : undefined}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '4px 8px 16px',
            background: 'none',
            border: 'none',
            cursor: simLock ? 'not-allowed' : 'pointer',
          }}
        >
          <img src={asset('melio-symbol.svg')} width={28} height={28} alt="" />
          <Text textStyle="body2Semi">API</Text>
          <PennyPill status="brand" type="secondary" label="DEV" />
        </button>
        {DASH_NAV.map((sec, si) => (
          <div key={si} style={{ marginBottom: 14 }}>
            {sec.group && (
              <div className="eyebrow" style={{ padding: '4px 10px', color: 'var(--text-secondary)' }}>
                {sec.group}
              </div>
            )}
            <NavigationList orientation="vertical">
              {sec.items.map(([id, label, icon]) => {
                // Locked in production-pending: tabs stay visible but disabled (nothing here is
                // manageable). Locked while the Simulator is connected: these tabs would navigate
                // away and drop the live session, so they're disabled until the user disconnects.
                const locked = prodLocked || simLock;
                return (
                  <RailItem
                    key={id}
                    label={label}
                    icon={prodLocked ? 'lock' : icon}
                    selected={view === id && !prodLocked}
                    disabled={locked}
                    title={
                      simLock
                        ? 'Disconnect the simulator to switch sections'
                        : prodLocked
                          ? 'Available once your partner is approved for production'
                          : undefined
                    }
                    onClick={() => setView(id)}
                  />
                );
              })}
            </NavigationList>
          </div>
        ))}
        <div style={{ flex: 1 }} />
        <NavigationList orientation="vertical">
          {/* Simulator — pinned to the bottom of the rail, above the utility links. It's a
              hands-on sandbox rather than a management section, so it sits apart from the
              Entities / Developers groups. Still reachable in production-pending (the console has
              its own key gate); disabled only there, and always active once selected. */}
          <RailItem
            label="Simulator"
            icon={prodLocked ? 'lock' : 'dashboard'}
            selected={inSimulator}
            disabled={prodLocked}
            title={prodLocked ? 'Available once your partner is approved for production' : undefined}
            onClick={() => setView('simulator')}
          />
          {/* Schedule a call — the beta-program calendar. Also offered on the production-access-pending
              panel, but kept in the rail so an already-approved partner can still book time from
              anywhere in the dashboard. */}
          <RailItem label="Schedule a call" icon="calendar" onClick={() => window.open(SCHEDULE_URL, '_blank', 'noopener,noreferrer')} />
          <RailItem label="Documentation" icon="document" onClick={() => go('/reference')} />
          <RailItem label="Log out" icon="exit" onClick={() => app.signOut()} />
        </NavigationList>
      </aside>

      {/* main */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* top bar */}
        <header
          style={{
            height: 64,
            flexShrink: 0,
            background: 'var(--surface-primary)',
            borderBottom: '1px solid var(--border-static)',
            display: 'flex',
            alignItems: 'center',
            padding: '0 24px',
            gap: 16,
          }}
        >
          <OrgName />
          <EnvSwitcher />
          <div style={{ flex: 1 }} />
          <IconButton name="help-circle" ariaLabel="Help" />
        </header>

        {/* penny-backed: <SectionBanner variant="critical"> (via the ds Banner wrapper), which brings
            the warning icon and the critical treatment with it. */}
        {app.isLive && (
          <Banner kind="critical" style={{ padding: '8px 24px' }}>
            You&apos;re in live mode — payments here move real money.
          </Banner>
        )}

        {/* content — the Simulator owns its own scrolling 3-column console, so it renders
            full-bleed and height-filling, outside the padded/max-width wrapper the other
            sections share. */}
        {inSimulator ? (
          <PayoutsSimulator embedded onConnectionChange={setSimConn} />
        ) : (
          <div style={{ flex: 1, overflowY: 'auto', padding: '32px 32px 60px' }}>
            <div style={{ maxWidth: 1080, margin: '0 auto' }}>
              {prodLocked ? (
                <ProductionAccessPending />
              ) : view === 'keys' ? (
                <KeysView />
              ) : view === 'webhooks' ? (
                <WebhooksView />
              ) : (
                <BusinessesView entityId={id} go={go} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export { Drawer, Table, StatCard, PageTitle, Dashboard };
